import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { qk } from "@/api/query-keys";

/** プロセスグループ（作成フォームの選択肢と、空グループの検出に使う）。 */
export type ProcessGroup = {
  readonly id: string;
  readonly displayName: string;
};

type SpiffProcessGroupRow = {
  readonly id?: string | null;
  readonly display_name?: string | null;
  readonly process_groups?: readonly SpiffProcessGroupRow[];
};

/**
 * グループ一覧。
 *
 * モデル一覧（use-design-models）の `groupPath` からもグループは拾えるが、
 * それだと**モデルが 1 つも無いグループが出ない**。作ったばかりのグループに最初の
 * モデルを作れず、空グループの削除の導線も出せないので、グループは専用 API から取る。
 *
 * 入れ子は平坦にして返す（作成フォームの選択肢と空グループの検出に使う。階層は
 * id（`hr/internal`）から組み立て直せる）。
 */
export async function fetchProcessGroups(
  client: ApiClient,
): Promise<readonly ProcessGroup[]> {
  const response = await client.get<{
    readonly results?: readonly SpiffProcessGroupRow[];
  }>("/process-groups", { query: { per_page: 100, page: 1 } });

  return flatten(response.results ?? []).sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );
}

function flatten(rows: readonly SpiffProcessGroupRow[]): ProcessGroup[] {
  return rows.flatMap((row) => {
    const children = flatten(row.process_groups ?? []);
    // id が無い行はパスに載せられないので候補にしない
    if (typeof row.id !== "string" || row.id === "") return children;

    const name = row.display_name ?? "";
    const displayName = name === "" ? row.id : name;

    return [{ id: row.id, displayName }, ...children];
  });
}

/**
 * ApiClient はキャッシュキーではないので queryKey に含めない
 * （use-permissions と同じく、取得関数を外で作って渡す）。
 */
export function processGroupsQueryOptions(client: ApiClient) {
  const fetchGroups = () => fetchProcessGroups(client);

  return queryOptions({
    queryKey: qk.processGroups.list(),
    queryFn: fetchGroups,
    staleTime: 5 * 60_000,
  });
}

export function useProcessGroups(): UseQueryResult<readonly ProcessGroup[]> {
  return useQuery(processGroupsQueryOptions(useApiClient()));
}
